import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Logo as SiteLogo } from '../../components/Logo';
import { FontFiles } from '../../globals';
import { getMemoramaParticipants } from '../../services/memoramaApi';
import { getCompanyParticipation } from '../../utils/memoramaCompanies';
import { MemoryGlobal, Shell, TopLine, Header, RecordsContent, RecordsHeader, RecordsTitle, RecordsSubtitle, TableWrapper, RecordsTable, StatusMessage, RefreshButton, RecordsActions, RecordsStats, RecordsStat } from '../MemoryGamePage/MemoryGamePage.styles';

const parseExtras = (extras) => {
  if (!extras) return {};
  if (typeof extras === 'object') return extras;
  try { return JSON.parse(extras); } catch { return {}; }
};

const normalizeRecords = (records) => records.map((record, index) => {
  const extras = parseExtras(record.extras);
  return {
    id: record.id || record._id || index,
    fullName: record.full_name || record.fullName || '-',
    email: record.email || '-',
    phone: record.phone || '-',
    company: record.company || '-',
    pairs: extras.aciertos ?? record.aciertos ?? '-',
    prize: extras.premio ?? record.premio ?? 'Sin premio',
  };
});

const getStatistics = (records) => {
  const prizes = new Map();
  records.forEach(record => {
    const prize = String(record.prize).replace(/^Ganaste\s+(?:un|una)\s+/i, '').trim().toLowerCase();
    if (!prize || prize === 'sin premio' || prize === '-') return;
    prizes.set(prize, (prizes.get(prize) || 0) + 1);
  });
  const mostWonCount = Math.max(0, ...prizes.values());
  const mostWonPrizes = [...prizes.entries()]
    .filter(([, count]) => count === mostWonCount)
    .map(([prize]) => prize.charAt(0).toUpperCase() + prize.slice(1));
  const companies = getCompanyParticipation(records);
  const topCompanyCount = Math.max(0, ...companies.map(company => company.count));
  const topCompanies = companies.filter(company => company.count === topCompanyCount);
  return {
    total: records.length,
    topCompany: topCompanies.map(company => company.name).join(' / ') || 'Sin empresas aún',
    topCompanyCount,
    companiesTied: topCompanies.length > 1,
    mostWonPrize: mostWonPrizes.join(' / ') || 'Sin premios aún',
    mostWonCount,
    tied: mostWonPrizes.length > 1,
  };
};

export default function MemoryGameRegisterPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const statistics = useMemo(() => getStatistics(records), [records]);

  const loadRecords = async () => {
    setLoading(true);
    setError('');
    try { setRecords(normalizeRecords(await getMemoramaParticipants())); }
    catch { setError('No se pudieron cargar los registros. Intenta nuevamente.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRecords(); }, []);

  const downloadExcel = () => {
    const rows = records.map(record => ({
      Nombre: record.fullName,
      Email: record.email,
      Teléfono: record.phone,
      Empresa: record.company,
      Aciertos: record.pairs,
      Premio: record.prize,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Participantes');
    XLSX.writeFile(workbook, 'memorama-participantes.xlsx');
  };

  return <Shell>
    <FontFiles /><MemoryGlobal /><TopLine /><Header><SiteLogo /></Header>
    <RecordsContent>
      <RecordsHeader>
        <div><RecordsTitle>Registros del memorama</RecordsTitle><RecordsSubtitle>Participantes y resultados de la dinámica.</RecordsSubtitle></div>
        <RecordsActions><RefreshButton type="button" onClick={loadRecords}>Actualizar</RefreshButton><RefreshButton type="button" onClick={downloadExcel} disabled={!records.length}>Descargar Excel</RefreshButton></RecordsActions>
      </RecordsHeader>
      {loading && <StatusMessage>Cargando registros...</StatusMessage>}
      {error && <StatusMessage error>{error}</StatusMessage>}
      {!loading && !error && <>
        <RecordsStats aria-label="Estadísticas del memorama">
          <RecordsStat><h2>Premio más ganado</h2><strong>{statistics.mostWonPrize}</strong><p>{statistics.mostWonCount ? `${statistics.mostWonCount} participante${statistics.mostWonCount === 1 ? '' : 's'}${statistics.tied ? ' por premio (empate)' : ''}` : 'Todavía no hay ganadores'}</p></RecordsStat>
          <RecordsStat><h2>Total de participantes</h2><strong>{statistics.total}</strong><p>Registros del memorama</p></RecordsStat>
          <RecordsStat><h2>Empresa con mayor participación</h2><strong>{statistics.topCompany}</strong><p>{statistics.topCompanyCount ? `${statistics.topCompanyCount} ${statistics.topCompanyCount === 1 ? 'participación' : 'participaciones'}${statistics.companiesTied ? ' por empresa (empate)' : ''}` : 'Todavía no hay empresas registradas'}</p></RecordsStat>
        </RecordsStats>
        <TableWrapper><RecordsTable><thead><tr><th>Nombre</th><th>Email</th><th>Teléfono</th><th>Empresa</th><th>Aciertos</th><th>Premio</th></tr></thead><tbody>{records.length ? records.map(record => <tr key={record.id}><td>{record.fullName}</td><td>{record.email}</td><td>{record.phone}</td><td>{record.company}</td><td>{record.pairs}</td><td>{record.prize}</td></tr>) : <tr><td colSpan="6">No hay registros todavía.</td></tr>}</tbody></RecordsTable></TableWrapper>
      </>}
    </RecordsContent>
  </Shell>;
}
